<script setup lang="ts" generic="T = any">
import { useFieldTexts, useValidationRules } from '@/composables';
import { useValidationErrorStore } from '@/stores/validationErrorStore';
import {
    useAttrs, computed, ref, onMounted,
} from 'vue';
import type { VSelect } from 'vuetify/components';
import { generateInputId } from '@/utils/idUtils';

type PropsType = {
    modelValue?: T;
    items?: any[];
    label?: string;
    placeholder?: string;
    variant?: VSelect['$props']['variant'];
    density?: VSelect['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    itemTitle?: string;
    itemValue?: string;
    multiple?: boolean;
    chips?: boolean;
    closableChips?: boolean;
    fieldName?: string;
    panelIndex?: number;
    tabId?: string;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: false,
    loading: false,
    isLabelTranslated: true,
    showRequiredVisual: true,
    itemTitle: 'title',
    itemValue: 'value',
    multiple: false,
    chips: false,
    closableChips: false,
    dataType: 'select',
});

const emit = defineEmits<{
    'update:modelValue': [value: T | null];
    'click:clear': [];
    'click:append': [];
    'click:prepend': [];
    'click:appendInner': [];
    'click:prependInner': [];
    focus: [event: FocusEvent];
    blur: [event: FocusEvent];
    'update:menu': [value: boolean];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
});

const { rules, isRequired } = useValidationRules(attrs);

const validationStore = useValidationErrorStore();
const resolvedFieldName = props.fieldName || props.label || 'field';

const inputValidation = validationStore.currentFormId
    ? validationStore.registerInput(resolvedFieldName, props.panelIndex ?? null, props.tabId ?? null)
    : null;

const computedId = computed(() => {
    return inputValidation?.inputId || generateInputId('select');
});

const selectRef = ref<VSelect | null>(null);

onMounted(() => {
    const input = selectRef.value?.$el?.querySelector('input');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VSelect
        ref="selectRef"
        :id="computedId"
        :model-value="modelValue"
        :items="items"
        :label="getLabel"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :item-title="itemTitle"
        :item-value="itemValue"
        :multiple="multiple"
        :chips="chips"
        :closable-chips="closableChips"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:model-value="emit('update:modelValue', $event)"
        @click:clear="emit('click:clear')"
        @click:append="emit('click:append')"
        @click:prepend="emit('click:prepend')"
        @click:append-inner="emit('click:appendInner')"
        @click:prepend-inner="emit('click:prependInner')"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @update:menu="emit('update:menu', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots.prependInner"
            #prepend-inner
        >
            <slot name="prepend-inner" />
        </template>

        <template
            v-if="$slots.appendInner"
            #append-inner
        >
            <slot name="append-inner" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>

        <template
            v-if="$slots.item"
            #item="{ props: itemProps, item }"
        >
            <slot
                name="item"
                :props="itemProps"
                :item="item"
            />
        </template>

        <template
            v-if="$slots.chip"
            #chip="{ props: chipProps, item, index }"
        >
            <slot
                name="chip"
                :props="chipProps"
                :item="item"
                :index="index"
            />
        </template>

        <template
            v-if="$slots.selection"
            #selection="{ item, index }"
        >
            <slot
                name="selection"
                :item="item"
                :index="index"
            />
        </template>
    </VSelect>
</template>
